import React from "react";
import { createRoot } from "react-dom/client";
import ArrowRight from "lucide-react/dist/esm/icons/arrow-right.js";
import BadgeDollarSign from "lucide-react/dist/esm/icons/badge-dollar-sign.js";
import Bell from "lucide-react/dist/esm/icons/bell.js";
import Check from "lucide-react/dist/esm/icons/check.js";
import ChevronRight from "lucide-react/dist/esm/icons/chevron-right.js";
import Code2 from "lucide-react/dist/esm/icons/code-2.js";
import Copy from "lucide-react/dist/esm/icons/copy.js";
import Database from "lucide-react/dist/esm/icons/database.js";
import FileText from "lucide-react/dist/esm/icons/file-text.js";
import Hammer from "lucide-react/dist/esm/icons/hammer.js";
import Leaf from "lucide-react/dist/esm/icons/leaf.js";
import LogOut from "lucide-react/dist/esm/icons/log-out.js";
import Mail from "lucide-react/dist/esm/icons/mail.js";
import PawPrint from "lucide-react/dist/esm/icons/paw-print.js";
import Rocket from "lucide-react/dist/esm/icons/rocket.js";
import Save from "lucide-react/dist/esm/icons/save.js";
import Settings2 from "lucide-react/dist/esm/icons/settings-2.js";
import Sparkles from "lucide-react/dist/esm/icons/sparkles.js";
import Truck from "lucide-react/dist/esm/icons/truck.js";
import UserRound from "lucide-react/dist/esm/icons/user-round.js";
import Waves from "lucide-react/dist/esm/icons/waves.js";
import { calculateQuote, defaultValues, templateById, templates, type QuoteTemplate, type QuoteValues } from "./shared/templates";
import "./styles.css";

const stripeLinks = {
  starter: "https://buy.stripe.com/5kQ28qgqu4vR5pUau3a7C07",
  hosted: "https://buy.stripe.com/aFabJ0a265zV4lQau3a7C08"
};

const iconMap = {
  sparkles: Sparkles,
  leaf: Leaf,
  truck: Truck,
  waves: Waves,
  hammer: Hammer,
  paw: PawPrint
};

interface AdminLead {
  id: string;
  service_type: string;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  postcode: string | null;
  quote_low: number;
  quote_high: number;
  source: string;
  status: string;
  created_at: string;
}

interface EmailInfo {
  provider: string;
  configured: boolean;
  fromEmail: string | null;
  notifyEmail: string | null;
  cloudflareBindingReady: boolean;
  cloudflareEmailSendingReady: boolean;
  resendReady: boolean;
}

interface AccountUser {
  id: string;
  email: string;
  plan: string;
}

interface QuotaInfo {
  savedCount: number;
  freeLimit: number;
  remainingFree: number;
  paid: boolean;
}

interface SavedQuotation {
  id: string;
  service_type: string;
  title: string;
  customer: { name?: string; email?: string; phone?: string; postcode?: string; notes?: string };
  quote: { low: number; high: number; midpoint: number };
  created_at: string;
}

function App() {
  const route = window.location.pathname;

  if (route === "/account") {
    return <AccountPage />;
  }

  if (route === "/admin") {
    return <AdminPage />;
  }

  if (route === "/success") {
    return <StatusPage kind="success" />;
  }

  if (route === "/cancel") {
    return <StatusPage kind="cancel" />;
  }

  if (route === "/embed-demo") {
    return <EmbedDemoPage />;
  }

  return <LandingPage />;
}

function LandingPage() {
  const [selectedId, setSelectedId] = React.useState("cleaning");
  const selectedTemplate = templateById(selectedId);

  return (
    <>
      <header className="site-header">
        <a className="brand" href="/">
          <span className="brand-mark">Q</span>
          <span>QuoteKit Local</span>
        </a>
        <nav aria-label="Primary navigation">
          <a href="#templates">Templates</a>
          <a href="#pricing">Pricing</a>
          <a href="/account">Account</a>
          <a href="/admin">Admin</a>
        </nav>
        <a className="nav-cta" href="#pricing">
          Start selling
          <ArrowRight size={16} />
        </a>
      </header>

      <main>
        <section className="hero-section">
          <div className="hero-copy">
            <h1>Instant quote calculators for small local service businesses.</h1>
            <p>
              Sell a ready-made calculator template or host it for cleaners, lawn care, movers, pool service,
              renovation operators, and pet groomers. Leads land in the database and trigger email notifications.
            </p>
            <div className="hero-actions">
              <a className="button primary" href="#pricing">
                View paid plans
                <BadgeDollarSign size={18} />
              </a>
              <a className="button secondary" href="/embed-demo">
                See embed demo
                <Code2 size={18} />
              </a>
              <a className="button secondary" href="/account">
                Save quotes
                <UserRound size={18} />
              </a>
            </div>
            <div className="proof-row" aria-label="Product capabilities">
              <span>
                <Database size={16} />
                Supabase-ready
              </span>
              <span>
                <Mail size={16} />
                Lead email alerts
              </span>
              <span>
                <Rocket size={16} />
                Cloudflare deploy
              </span>
            </div>
          </div>
          <div className="hero-visual">
            <img src="/images/quote-kit-hero.png" alt="Local service owner reviewing a quote calculator on laptop and phone" />
            <div className="hero-float quote-card">
              <span>Average lead quote</span>
              <strong>$155 - $210</strong>
              <small>Cleaning template, deep clean, windows included</small>
            </div>
          </div>
        </section>

        <section className="calculator-band" id="templates">
          <div className="section-heading">
            <span className="section-label">Reusable calculator engine</span>
            <h2>Pick an industry and the quote logic updates instantly.</h2>
          </div>
          <div className="workbench">
            <aside className="template-rail" aria-label="Industry templates">
              {templates.map((template) => (
                <button
                  className={template.id === selectedId ? "template-tab active" : "template-tab"}
                  key={template.id}
                  onClick={() => setSelectedId(template.id)}
                  type="button"
                >
                  <TemplateIcon template={template} />
                  <span>{template.shortLabel}</span>
                </button>
              ))}
            </aside>
            <QuotePanel template={selectedTemplate} mode="marketing" />
          </div>
        </section>

        <section className="feature-grid" aria-label="What customers get">
          <Feature
            icon={<Settings2 size={22} />}
            title="Editable price tables"
            copy="Owners can tune base fees, unit prices, optional add-ons, minimums, and quote confidence buffers."
          />
          <Feature
            icon={<Code2 size={22} />}
            title="Embeddable widget"
            copy="A single script tag renders the calculator on WordPress, Webflow, Framer, or a plain HTML site."
          />
          <Feature
            icon={<Bell size={22} />}
            title="Lead notifications"
            copy="Every quote request can be stored in Supabase and sent to the business owner by email."
          />
        </section>

        <section className="pricing-section" id="pricing">
          <div className="section-heading compact">
            <span className="section-label">Stripe powered</span>
            <h2>Simple pricing that feels worth buying.</h2>
          </div>
          <div className="pricing-grid">
            <PricingCard
              title="Starter Template Pack"
              price="$29"
              cadence="one time"
              copy="Downloadable template package for builders who want to self-host or customize."
              href={stripeLinks.starter}
              features={["Six industry calculators", "Embed script", "Supabase schema", "Email notification code"]}
            />
            <PricingCard
              title="Hosted QuoteKit"
              price="$9"
              cadence="per month"
              copy="Hosted calculator, managed price table, lead database, and email notification path."
              href={stripeLinks.hosted}
              featured
              features={["Managed Cloudflare hosting", "Owner price table", "Lead inbox", "Priority template updates"]}
            />
          </div>
        </section>

        <section className="seo-section">
          <div>
            <span className="section-label">Passive acquisition plan</span>
            <h2>Built for search, template marketplaces, GitHub demos, and community discovery.</h2>
          </div>
          <div className="seo-list">
            <p>Publish landing pages around cleaning quote calculator, lawn mowing estimator, moving cost form, pool service estimate, handyman quote form, and pet grooming price calculator.</p>
            <p>Use the demo as the proof artifact. Buyers do not need a sales call; they can inspect the calculator, pay, and start from the admin screen or embedded snippet.</p>
            <div className="seo-link-list" aria-label="Industry quote calculator pages">
              <a href="/cleaning-quote-calculator">Cleaning quote calculator</a>
              <a href="/lawn-care-quote-calculator">Lawn care quote calculator</a>
              <a href="/moving-quote-calculator">Moving quote calculator</a>
              <a href="/pool-service-quote-calculator">Pool service quote calculator</a>
              <a href="/handyman-quote-calculator">Handyman quote calculator</a>
              <a href="/pet-grooming-quote-calculator">Pet grooming quote calculator</a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

function QuotePanel({ template, mode, onSaved }: { template: QuoteTemplate; mode: "marketing" | "admin" | "embed" | "account"; onSaved?: () => void }) {
  const [values, setValues] = React.useState<QuoteValues>(() => defaultValues(template));
  const [lead, setLead] = React.useState({ name: "", email: "", phone: "", postcode: "", notes: "" });
  const [status, setStatus] = React.useState<string | null>(null);
  const [upgradeUrl, setUpgradeUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    setValues(defaultValues(template));
    setStatus(null);
    setUpgradeUrl(null);
  }, [template]);

  const result = calculateQuote(template, values);

  async function submitLead(event: React.FormEvent) {
    event.preventDefault();
    setUpgradeUrl(null);
    setStatus(mode === "account" ? "Saving quotation..." : "Saving lead...");
    try {
      const response = await fetch(mode === "account" ? "/api/quotes/save" : "/api/quote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenantSlug: "demo-clean",
          serviceType: template.id,
          values,
          customer: lead,
          source: mode
        })
      });
      const payload = await response.json() as {
        ok?: boolean;
        quote?: { low: number; high: number };
        quotation?: { quote?: { low: number; high: number } };
        emailStatus?: string;
        error?: string;
        checkoutUrl?: string;
        quota?: QuotaInfo;
      };
      if (!response.ok || !payload.ok) {
        if (response.status === 402 && payload.checkoutUrl) {
          setUpgradeUrl(payload.checkoutUrl);
        }
        throw new Error(payload.error ?? "Quote request failed");
      }
      if (mode === "account") {
        const savedQuote = payload.quotation?.quote;
        const remaining = payload.quota?.paid ? "unlimited saves" : `${payload.quota?.remainingFree ?? 0} free saves left`;
        setStatus(`Quotation saved. $${savedQuote?.low} - $${savedQuote?.high}. ${remaining}.`);
        onSaved?.();
      } else {
        setStatus(`Lead saved. Quote range $${payload.quote?.low} - $${payload.quote?.high}. Email: ${payload.emailStatus ?? "queued"}.`);
      }
    } catch (error) {
      setStatus(error instanceof Error ? error.message : mode === "account" ? "Quotation save failed" : "Lead request failed");
    }
  }

  return (
    <div className={mode === "embed" ? "quote-panel embedded" : "quote-panel"}>
      <div className="quote-head">
        <div>
          <span className="panel-label">{template.label}</span>
          <h3>{template.summary}</h3>
        </div>
        <div className="quote-total">
          <span>Estimate</span>
          <strong>${result.low} - ${result.high}</strong>
        </div>
      </div>

      <form className="quote-form" onSubmit={submitLead}>
        <div className="field-grid">
          {template.fields.map((field) => (
            <label className="field" key={field.id}>
              <span>
                {field.label}
                {field.unit ? <small>{field.unit}</small> : null}
              </span>
              {field.kind === "number" ? (
                <input
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  type="number"
                  value={Number(values[field.id] ?? field.defaultValue)}
                  onChange={(event) => setValues((current) => ({ ...current, [field.id]: Number(event.target.value) }))}
                />
              ) : null}
              {field.kind === "select" ? (
                <select
                  value={String(values[field.id] ?? field.defaultValue)}
                  onChange={(event) => setValues((current) => ({ ...current, [field.id]: event.target.value }))}
                >
                  {field.options?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : null}
              {field.kind === "boolean" ? (
                <input
                  checked={Boolean(values[field.id])}
                  type="checkbox"
                  onChange={(event) => setValues((current) => ({ ...current, [field.id]: event.target.checked }))}
                />
              ) : null}
            </label>
          ))}
        </div>

        <div className="lead-box">
          <h4>Capture the lead</h4>
          <div className="lead-fields">
            <input placeholder="Name" value={lead.name} onChange={(event) => setLead((current) => ({ ...current, name: event.target.value }))} />
            <input placeholder="Email" type="email" value={lead.email} onChange={(event) => setLead((current) => ({ ...current, email: event.target.value }))} />
            <input placeholder="Phone" value={lead.phone} onChange={(event) => setLead((current) => ({ ...current, phone: event.target.value }))} />
            <input placeholder="Postcode" value={lead.postcode} onChange={(event) => setLead((current) => ({ ...current, postcode: event.target.value }))} />
          </div>
          <textarea placeholder="Anything the business should know?" value={lead.notes} onChange={(event) => setLead((current) => ({ ...current, notes: event.target.value }))} />
          <button className="button primary full" type="submit">
            {mode === "account" ? "Save quotation" : "Send quote request"}
            {mode === "account" ? <Save size={18} /> : <ChevronRight size={18} />}
          </button>
          {status ? <p className="form-status">{status}</p> : null}
          {upgradeUrl ? (
            <a className="button secondary full" href={upgradeUrl}>
              Upgrade to keep saving
              <BadgeDollarSign size={18} />
            </a>
          ) : null}
        </div>
      </form>
    </div>
  );
}

function AccountPage() {
  const [selectedId, setSelectedId] = React.useState("cleaning");
  const [authMode, setAuthMode] = React.useState<"login" | "signup">("login");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [user, setUser] = React.useState<AccountUser | null>(null);
  const [quota, setQuota] = React.useState<QuotaInfo | null>(null);
  const [quotations, setQuotations] = React.useState<SavedQuotation[]>([]);
  const [status, setStatus] = React.useState<string | null>(null);
  const template = templateById(selectedId);

  React.useEffect(() => {
    void loadMe();
  }, []);

  async function loadMe() {
    const response = await fetch("/api/auth/me");
    const payload = await response.json() as { ok?: boolean; user?: AccountUser | null; quota?: QuotaInfo };
    if (payload.user) {
      setUser(payload.user);
      setQuota(payload.quota ?? null);
      await loadQuotations();
    }
  }

  async function submitAuth(event: React.FormEvent) {
    event.preventDefault();
    setStatus(authMode === "signup" ? "Creating account..." : "Logging in...");
    try {
      const response = await fetch(authMode === "signup" ? "/api/auth/signup" : "/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });
      const payload = await response.json() as { ok?: boolean; user?: AccountUser; quota?: QuotaInfo; error?: string };
      if (!response.ok || !payload.ok || !payload.user) {
        throw new Error(payload.error ?? "Authentication failed");
      }
      setUser(payload.user);
      setQuota(payload.quota ?? null);
      setStatus("Logged in.");
      await loadQuotations();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Authentication failed");
    }
  }

  async function loadQuotations() {
    try {
      const response = await fetch("/api/quotes/mine");
      const payload = await response.json() as { ok?: boolean; quotations?: SavedQuotation[]; quota?: QuotaInfo; user?: AccountUser; error?: string };
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error ?? "Saved quotations failed to load");
      }
      setQuotations(payload.quotations ?? []);
      setQuota(payload.quota ?? null);
      if (payload.user) {
        setUser(payload.user);
      }
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Saved quotations failed to load");
    }
  }

  async function logout() {
    await fetch("/api/auth/logout", { method: "POST" });
    setUser(null);
    setQuota(null);
    setQuotations([]);
    setStatus("Logged out.");
  }

  return (
    <main className="account-shell">
      <section className="account-panel">
        <a className="back-link" href="/">
          <ArrowRight size={16} />
          Back to site
        </a>
        <div className="admin-heading">
          <span className="section-label">Quote account</span>
          <h1>Save up to 5 quotations for free.</h1>
          <p>Log in to keep client-ready estimates. Free accounts can save five quotations; paid accounts can keep saving without the free-plan cap.</p>
        </div>

        {!user ? (
          <form className="auth-card" onSubmit={submitAuth}>
            <div className="auth-tabs" role="tablist" aria-label="Authentication mode">
              <button className={authMode === "login" ? "auth-tab active" : "auth-tab"} onClick={() => setAuthMode("login")} type="button">
                Log in
              </button>
              <button className={authMode === "signup" ? "auth-tab active" : "auth-tab"} onClick={() => setAuthMode("signup")} type="button">
                Create account
              </button>
            </div>
            <label className="field">
              <span>Email</span>
              <input autoComplete="email" inputMode="email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
            </label>
            <label className="field">
              <span>Password</span>
              <input autoComplete={authMode === "signup" ? "new-password" : "current-password"} type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
            </label>
            <button className="button primary full" type="submit">
              {authMode === "signup" ? "Create account" : "Log in"}
              <UserRound size={18} />
            </button>
            {status ? <p className="form-status">{status}</p> : null}
          </form>
        ) : (
          <div className="account-grid">
            <div className="account-card">
              <div>
                <span className="section-label">Signed in</span>
                <h2>{user.email}</h2>
                <p className="muted-copy">{quota?.paid ? "Paid account" : `${quota?.remainingFree ?? 0} of ${quota?.freeLimit ?? 5} free saves left`}</p>
              </div>
              <div className="quota-grid">
                <div>
                  <span>Saved</span>
                  <strong>{quota?.savedCount ?? 0}</strong>
                </div>
                <div>
                  <span>Limit</span>
                  <strong>{quota?.paid ? "∞" : quota?.freeLimit ?? 5}</strong>
                </div>
              </div>
              <button className="button secondary full" onClick={logout} type="button">
                Log out
                <LogOut size={18} />
              </button>
            </div>

            <div className="account-card">
              <h2>Saved quotations</h2>
              <button className="button secondary full" onClick={loadQuotations} type="button">
                Refresh saved quotes
                <FileText size={18} />
              </button>
              <div className="saved-list">
                {quotations.length === 0 ? <p className="muted-copy">No saved quotations yet.</p> : null}
                {quotations.map((quotation) => (
                  <article className="lead-item" key={quotation.id}>
                    <div>
                      <strong>{quotation.title}</strong>
                      <span>{quotation.customer?.name || "No customer name"} · {quotation.service_type}</span>
                    </div>
                    <small>{new Date(quotation.created_at).toLocaleString()}</small>
                  </article>
                ))}
              </div>
            </div>

            <div className="account-card wide">
              <h2>Build and save a quotation</h2>
              <div className="account-template-list">
                {templates.map((item) => (
                  <button className={item.id === selectedId ? "admin-template active" : "admin-template"} key={item.id} onClick={() => setSelectedId(item.id)} type="button">
                    <TemplateIcon template={item} />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
              <QuotePanel template={template} mode="account" onSaved={loadQuotations} />
            </div>
          </div>
        )}
      </section>
    </main>
  );
}

function AdminPage() {
  const [selectedId, setSelectedId] = React.useState("cleaning");
  const [token, setToken] = React.useState("");
  const [businessName, setBusinessName] = React.useState("Demo Cleaning Co");
  const [ownerEmail, setOwnerEmail] = React.useState("owner@example.com");
  const [status, setStatus] = React.useState<string | null>(null);
  const [emailInfo, setEmailInfo] = React.useState<EmailInfo | null>(null);
  const [leads, setLeads] = React.useState<AdminLead[]>([]);
  const template = templateById(selectedId);
  const embedCode = `<script src="${window.location.origin}/embed.js" data-tenant="demo-clean" data-service="${template.id}" defer></script>`;

  async function saveTenant() {
    setStatus("Saving tenant config...");
    try {
      const response = await fetch("/api/admin/tenant", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-admin-token": token
        },
        body: JSON.stringify({
          slug: "demo-clean",
          businessName,
          ownerEmail,
          primaryColor: "#176b5b",
          services: templates
        })
      });
      const payload = await response.json() as { ok?: boolean; error?: string };
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error ?? "Save failed");
      }
      setStatus("Saved to Supabase.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Save failed");
    }
  }

  async function copyEmbed() {
    await navigator.clipboard.writeText(embedCode);
    setStatus("Embed code copied.");
  }

  async function loadLeads() {
    setStatus("Loading recent leads...");
    try {
      const response = await fetch("/api/admin/leads?tenant=demo-clean&limit=10", {
        headers: { "x-admin-token": token }
      });
      const payload = await response.json() as { ok?: boolean; leads?: AdminLead[]; error?: string };
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error ?? "Lead load failed");
      }
      setLeads(payload.leads ?? []);
      setStatus(`Loaded ${payload.leads?.length ?? 0} leads.`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Lead load failed");
    }
  }

  async function checkEmailStatus() {
    setStatus("Checking email provider...");
    try {
      const response = await fetch("/api/admin/email-status", {
        headers: { "x-admin-token": token }
      });
      const payload = await response.json() as ({ ok?: boolean; error?: string } & Partial<EmailInfo>);
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error ?? "Email status check failed");
      }
      setEmailInfo({
        provider: payload.provider ?? "none",
        configured: Boolean(payload.configured),
        fromEmail: payload.fromEmail ?? null,
        notifyEmail: payload.notifyEmail ?? null,
        cloudflareBindingReady: Boolean(payload.cloudflareBindingReady),
        cloudflareEmailSendingReady: Boolean(payload.cloudflareEmailSendingReady),
        resendReady: Boolean(payload.resendReady)
      });
      setStatus(payload.configured ? "Email provider is configured." : "Email provider is not configured yet.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Email status check failed");
    }
  }

  async function sendTestEmail() {
    setStatus("Sending test email...");
    try {
      const response = await fetch("/api/admin/test-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-admin-token": token
        },
        body: JSON.stringify({ tenantSlug: "demo-clean", to: ownerEmail })
      });
      const payload = await response.json() as { ok?: boolean; emailStatus?: { provider: string; status: string }; error?: string };
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error ?? payload.emailStatus?.status ?? "Test email failed");
      }
      setStatus(`Test email sent with ${payload.emailStatus?.provider}.`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Test email failed");
    }
  }

  return (
    <main className="admin-shell">
      <section className="admin-panel">
        <a className="back-link" href="/">
          <ArrowRight size={16} />
          Back to site
        </a>
        <div className="admin-heading">
          <span className="section-label">Owner workspace</span>
          <h1>Price table and embed manager</h1>
          <p>Use this as the hosted-plan admin surface. The demo saves to Supabase when the Worker has service credentials and the admin token is supplied.</p>
        </div>

        <div className="admin-grid">
          <div className="admin-card">
            <h2>Business setup</h2>
            <label className="field">
              <span>Business name</span>
              <input value={businessName} onChange={(event) => setBusinessName(event.target.value)} />
            </label>
            <label className="field">
              <span>Lead email</span>
              <input value={ownerEmail} onChange={(event) => setOwnerEmail(event.target.value)} />
            </label>
            <label className="field">
              <span>Admin token</span>
              <input placeholder="Only needed to save" value={token} onChange={(event) => setToken(event.target.value)} />
            </label>
            <button className="button primary full" onClick={saveTenant} type="button">
              Save config
              <Database size={18} />
            </button>
            {status ? <p className="form-status">{status}</p> : null}
          </div>

          <div className="admin-card">
            <h2>Template picker</h2>
            <div className="admin-template-list">
              {templates.map((item) => (
                <button className={item.id === selectedId ? "admin-template active" : "admin-template"} key={item.id} onClick={() => setSelectedId(item.id)} type="button">
                  <TemplateIcon template={item} />
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="admin-card">
            <h2>Email notification</h2>
            <div className={emailInfo?.configured ? "status-pill ready" : "status-pill"}>
              {emailInfo?.configured ? "Provider ready" : "Provider not configured"}
            </div>
            <div className="status-table">
              <span>Provider</span>
              <strong>{emailInfo?.provider ?? "unknown"}</strong>
              <span>From address</span>
              <strong>{emailInfo?.fromEmail ?? "not set"}</strong>
              <span>Notify inbox</span>
              <strong>{emailInfo?.notifyEmail ?? "binding default"}</strong>
              <span>Cloudflare binding</span>
              <strong>{emailInfo?.cloudflareBindingReady ? "present" : "missing"}</strong>
              <span>Cloudflare API key</span>
              <strong>{emailInfo?.cloudflareEmailSendingReady ? "present" : "missing"}</strong>
              <span>Resend key</span>
              <strong>{emailInfo?.resendReady ? "present" : "missing"}</strong>
            </div>
            <button className="button secondary full" onClick={checkEmailStatus} type="button">
              Check email status
              <Mail size={18} />
            </button>
            <button className="button secondary full" onClick={sendTestEmail} type="button">
              Send test email
              <Bell size={18} />
            </button>
          </div>

          <div className="admin-card">
            <h2>Lead inbox</h2>
            <button className="button secondary full" onClick={loadLeads} type="button">
              Load recent leads
              <Database size={18} />
            </button>
            <div className="lead-list">
              {leads.length === 0 ? <p className="muted-copy">No leads loaded yet.</p> : null}
              {leads.map((lead) => (
                <article className="lead-item" key={lead.id}>
                  <div>
                    <strong>{lead.customer_name || "Unnamed lead"}</strong>
                    <span>{lead.service_type} · ${lead.quote_low} - ${lead.quote_high}</span>
                  </div>
                  <small>{lead.customer_email || lead.customer_phone || lead.postcode || lead.source}</small>
                </article>
              ))}
            </div>
          </div>

          <div className="admin-card wide">
            <h2>Price rows</h2>
            <div className="price-table">
              <div className="price-row header">
                <span>Field</span>
                <span>Default</span>
                <span>Pricing</span>
              </div>
              {template.fields.map((field) => (
                <div className="price-row" key={field.id}>
                  <span>{field.label}</span>
                  <span>{String(field.defaultValue)}</span>
                  <span>{field.pricePerUnit ? `$${field.pricePerUnit} / ${field.unit ?? "unit"}` : field.options ? "Multiplier options" : `$${field.flatFee ?? 0} add-on`}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="admin-card wide">
            <h2>Embed snippet</h2>
            <pre className="code-block">{embedCode}</pre>
            <button className="button secondary" onClick={copyEmbed} type="button">
              Copy snippet
              <Copy size={18} />
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}

function EmbedDemoPage() {
  return (
    <main className="embed-demo">
      <section>
        <a className="back-link" href="/">
          <ArrowRight size={16} />
          Back to site
        </a>
        <span className="section-label">Embed preview</span>
        <h1>This is what a customer can paste into their own website.</h1>
        <div className="embed-frame">
          <QuotePanel template={templateById("cleaning")} mode="embed" />
        </div>
        <pre className="code-block">{`<script src="${window.location.origin}/embed.js" data-tenant="demo-clean" data-service="cleaning" defer></script>`}</pre>
      </section>
    </main>
  );
}

function StatusPage({ kind }: { kind: "success" | "cancel" }) {
  const isSuccess = kind === "success";
  return (
    <main className="status-page">
      <section className="status-card">
        <span className="brand-mark">{isSuccess ? "Q" : "!"}</span>
        <h1>{isSuccess ? "Payment complete" : "Checkout cancelled"}</h1>
        <p>{isSuccess ? "Stripe accepted the payment. The next step is provisioning the hosted calculator or sending the template pack." : "No payment was taken. You can return to the pricing section and choose a plan again."}</p>
        <a className="button primary" href="/">
          Return to QuoteKit
          <ArrowRight size={18} />
        </a>
      </section>
    </main>
  );
}

function PricingCard({ title, price, cadence, copy, href, features, featured = false }: { title: string; price: string; cadence: string; copy: string; href: string; features: string[]; featured?: boolean }) {
  return (
    <article className={featured ? "pricing-card featured" : "pricing-card"}>
      <div>
        <h3>{title}</h3>
        <p>{copy}</p>
      </div>
      <div className="price-line">
        <strong>{price}</strong>
        <span>{cadence}</span>
      </div>
      <ul>
        {features.map((feature) => (
          <li key={feature}>
            <Check size={16} />
            {feature}
          </li>
        ))}
      </ul>
      <a className={featured ? "button primary full" : "button secondary full"} href={href} rel="noreferrer" target="_blank">
        Pay with Stripe
        <ArrowRight size={18} />
      </a>
    </article>
  );
}

function Feature({ icon, title, copy }: { icon: React.ReactNode; title: string; copy: string }) {
  return (
    <article className="feature-card">
      <span className="feature-icon">{icon}</span>
      <h3>{title}</h3>
      <p>{copy}</p>
    </article>
  );
}

function TemplateIcon({ template }: { template: QuoteTemplate }) {
  const Icon = iconMap[template.icon as keyof typeof iconMap] ?? Sparkles;
  return <Icon size={18} />;
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
